import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Subject } from 'rxjs';
import { Observable } from 'rxjs/Rx';
import { filter } from 'rxjs/operators';
import { io, Socket } from 'socket.io-client-latest';
import { environment } from 'environments/environment';
import { AuthService } from 'shared/services/authentication.service';

/**
 * Shared socket client for the Job Message Console (SO Messaging Console / PO Messaging Console
 * under vms/job-message-console/team/billing). Modeled on PersonalConversationSocketService so it
 * follows the same connect/auth/reconnect conventions. It must be initialized once from a
 * component shared across all modules (app-nav-header) rather than from each console component,
 * so a single connection is reused by every subscriber via the exposed Observables below.
 *
 * TODO: environment.messageNotificationSocketUrl is a placeholder and needs the real backend URL.
 * TODO: the inbound/outbound event names below (message/subscribe_console/...) are placeholders
 * mirroring PersonalConversationSocketService until the backend event contract is confirmed.
 */
@Injectable({
  providedIn: 'root'
})
export class MessageNotificationSocketService {

  socket: Socket;
  lastConsole: string;
  interval;
  isStarted: boolean = false;
  notificationCount: number = 0;
  notificationLists: Array<any> = [];
  MAX_NOTIFICATION_LIST = 10;
  joinedRooms: any;
  private allNotificationLists: any[] = [];

  private newMessage = new Subject<any>();
  private socketConnection = new Subject<boolean>();
  private totalUnreadMessageNotification = new BehaviorSubject<number>(0);
  private messageNotificationList = new BehaviorSubject<any[]>([]);
  private messageHeaderNotificationList = new BehaviorSubject<any[]>([]);
  // BehaviorSubject (not Subject) so a job-message-console page created *after* the notification
  // was clicked (fresh navigation) still receives it once it subscribes, instead of missing an
  // event emitted before it existed.
  private pendingSelection = new BehaviorSubject<any>(null);


  private readonly apiBaseUrl = `${environment.messageNotificationSocketUrl}/global-notifications/api`;

  constructor(private auth: AuthService, private http: HttpClient) { }

  initializeSocket(roomNames?) {
    if (!this.isStarted) {
      this.isStarted = true;
      this.connectSocket(roomNames);
    } else {
      console.log('Global notification socket connected already....');
    }
    this.loadUnreadMessages();
  }

  /**
   * Fetches everything unread for the logged-in user from the backend at login (rather than
   * only what arrives live afterwards) and replays each one through handleIncomingMessage() -
   * the same pipeline a live socket 'new_message' event uses - so the badge count, notification
   * list, and every realtime consumer (sidebar, header bell, favicon, notification-header,
   * notification.component) all start out correct instead of empty until new messages arrive.
   */
  loadUnreadMessages() {
    this.http.get<any[]>(`${this.apiBaseUrl}/messages/unread`).subscribe(
      rows => {
        // Backend returns newest-first; handleIncomingMessage() unshifts, so replay oldest-first
        // to end up with the same newest-first order in notificationLists.
        [...(rows || [])].reverse().forEach(row => this.handleIncomingMessage(row));
      },
      error => {
        console.error('MessageNotificationSocketService: failed to load unread messages', error);
      }
    );
  }

  async connectSocket(roomNames) {
    const token = await this.auth.renewTokens();
    const url = environment.messageNotificationSocketUrl;
    if (!url) {
      this.isStarted = false;
      return;
    }

    const socket: Socket = io(url, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      auth: { Authorization: token }
    });
    this.socket = socket;


    socket.on('reconnect_attempt', (attempt) => {
      console.log('Job Message Console socket: trying to reconnect, attempt ', attempt);
    });

    socket.on('reconnect_error', (error) => {
      console.error('Job Message Console socket: error occurred while reconnecting ', error);
    });

    socket.on('connect_error', (error) => {
      console.error('Job Message Console socket: error occurred while connecting ', error);
    });

    socket.on('connect', () => {
      this.socketConnection.next(true);
      console.log('Job Message Console socket connected', socket.id);
      // Room membership is server-side/session-scoped, so re-join on every connect - this also
      // covers reconnects, since socket.io re-fires 'connect' after a successful reconnection.
      this.joinNotificationSocketRoom(roomNames);
    });

    socket.on('disconnect', reason => {
      console.log('Job Message Console socket disconnected =>', reason);
      this.socketConnection.next(false);
      // Server-side room membership doesn't survive a disconnect, so forget it too and let the
      // next 'connect' event re-join from scratch.
      this.joinedRooms = null;
    });

    socket.on('new_message', (msgData) => {
      console.log("new_message recieved",msgData)
      this.handleIncomingMessage(msgData);
    });

    // Local test fixtures standing in for the backend job-message-console payloads until
    // environment.messageNotificationSocketUrl is wired up. Staggered with setTimeout so they
    // arrive one at a time like a live socket.on('message', ...) push instead of all at once.
    // this.getSampleMessages().forEach((sample, index) => {
    //   setTimeout(() => this.handleIncomingMessage(sample), (index + 1) * 5000);
    // });
  }



  formateMessageObj(msgData){
    if (msgData && msgData.metadata) {
      return {
        id: msgData.id,
        sfdcId: msgData.metadata.pmsCase.sfdcId,
        CreatedDate: msgData.createdAt,
        Contact__c: msgData.metadata.sender.sfdcId,
        Case__c: msgData.metadata.caseSfdcId,
        Work_Order__c: msgData.metadata.workOrderId,
        Order__c: msgData.metadata.orderId,
        Case_Comment_Body__c: msgData.metadata.message,
        commentType: msgData.metadata.commentType,
        modelName: msgData.modelName , //`${msgData.subModelId.toUpperCase()} ${msgData.modelName}`,
        modelId: msgData.modelId,
        isPrivate: msgData.metadata.isPrivate,
        Is_Public_Job__c: msgData.metadata.isPublicJob,
        Is_Public__c: msgData.metadata.isPublic,
        Message_Posted_from_Partner__c: (msgData.messageMetadata && msgData.messageMetadata.postByType ) ? msgData.messageMetadata.postByType : '',
        // Is_System_Generated__c: undefined,
        // Resolution_Comment__c: undefined,
        Is_sent_by_mobile__c: msgData.metadata.isSentFromByMobile,
        //Serviceo_Chat_Id: msgData.metadata.isPublic,
        // readByMSP: undefined,
        // readByPMS: undefined,
        caseModelId: (msgData.metadata.case && msgData.metadata.case.id) ? msgData.metadata.case.id : "",
        UnReadMsg: false,
        recordFromBackend: msgData.metadata.recordFromBackend,
        subModelName: msgData.subModelName,
        partner: {
          Name: msgData.metadata.partner.name,
          id: msgData.metadata.partner.id,
          sfdcId: msgData.metadata.partner.sfdcId
        },
        vendor: {
          Name: msgData.metadata.vendor.name,
          id: msgData.metadata.vendor.id,
          sfdcId: msgData.metadata.vendor.sfdcId
        },
        worker: {
          sfdcId: msgData.metadata.workOrderId,
          contactSfdcId: msgData.metadata.sender.sfdcId
        },
        program: {
          Name: msgData.metadata.program.name,
          Project__c: msgData.metadata.program.programName,
          sfdcId: msgData.metadata.program.sfdcId
        },
        job: {
          Service_Dispatch_SLA_Priority__c: msgData.metadata.job.serviceDispatchSLAPriority,
          Iron_Case__c: msgData.metadata.job.ironCase,
          Iron_Job_num__c: msgData.metadata.job.ironJobNum,
          Job_Status_Internal__c: msgData.metadata.job.jobStatusInternal,
          csum__c: msgData.metadata.job.csum,
          workflowStatusId: msgData.metadata.job.workflowStatusId,
          RecordTypeId: msgData.metadata.job.recordTypeId,
          is_backfill_worker: msgData.metadata.job.isBackfillWorker,
          Partner_Case_Number__c: msgData.metadata.job.partnerCaseNumber,
          sfdcId: msgData.metadata.job.caseSfdcId,
          Case__c: msgData.metadata.job.sfdcId,
          Dispatch_Service_Resolution_Status__c: msgData.metadata.job.dispatchServiceResolutionStatus,
          Job_Subject__c: msgData.metadata.job.jobSubject,
          Jobsite__c: msgData.metadata.job.jobsiteSfdcId,
          Project_SOP__c: msgData.metadata.job.projectSOP,
          Sales_Order__c: msgData.metadata.job.salesOrder,
          Service_Schedule__c: msgData.metadata.job.serviceSchedule,
          Work_Order__c: msgData.metadata.job.workOrderSfdcId,
          PMS_Case_Number: msgData.metadata.job.PMSCaseNumber,
          Work_Order_Number: msgData.metadata.job.workOrderNumber,
          PMC_Case_Number: msgData.metadata.job.PMCCaseNumber,
          CKSW_BASE__Account__c: msgData.metadata.job.CKSWBASEAccount,
          caseModelId: (msgData.metadata.case && msgData.metadata.case.id) ? msgData.metadata.case.id : "",
          jobsite: {
            // Abbreviation__c: null,
            // Jobsite_ID__c: msgData.metadata.program.sfdcId,
            // Jobsite_Key_Contact__c: '0032L00001MxshCQAR',
            Name: msgData.metadata.jobsite.name,
            Time_Zone__c: msgData.metadata.jobsite.timeZone,
            sfdcId: msgData.metadata.jobsite.sfdcId,
            //GEO_Metro__c: msgData.metadata.program.sfdcId
          },
          purchaseOrder: {
            Status: msgData.metadata.purchaseOrder.status,
            Work_Order_num__c: msgData.metadata.purchaseOrder.workOrderNumber,
            sfdcId: msgData.metadata.purchaseOrder.sfdcId,
            Service_Dispatch__c: msgData.metadata.purchaseOrder.serviceDispatch
          },
          pmcCase: {
            sfdcId: msgData.metadata.pmcCase.sfdcId,
            CaseNumber: msgData.metadata.pmcCase.caseNumber,
            Case_Summary__c: msgData.metadata.pmcCase.caseSummary,
            Dispatch_SLA_Priority__c: msgData.metadata.pmcCase.dispatchSLAPriority,
            Dispatch_Service_Resolution_Status__c: msgData.metadata.pmcCase.dispatchServiceResolutionStatus,
            Partner_Case_Number__c: msgData.metadata.pmcCase.partnerCaseNumber
          },
          pmsCase: {
            sfdcId: msgData.metadata.pmsCase.sfdcId,
            CaseNumber: msgData.metadata.pmsCase.caseNumber,
            Case_Summary__c: msgData.metadata.pmsCase.caseSummary,
            Dispatch_SLA_Priority__c: msgData.metadata.pmsCase.dispatchSLAPriority,
            Dispatch_Service_Resolution_Status__c: msgData.metadata.pmsCase.dispatchServiceResolutionStatus,
            Partner_Case_Number__c: msgData.metadata.pmsCase.partnerCaseNumber,
            Status: msgData.metadata.pmsCase.status,
            isFRU: msgData.metadata.pmsCase.isFRU
          },
          order:{
          ...msgData.metadata.order
          }
        },
        workOrder: {
          sfdcId: msgData.metadata.workOrder.sfdcId,
          Name: msgData.metadata.workOrder.name,
          Status__c: msgData.metadata.workOrder.status,
          Has_Backfill_Worker__c: msgData.metadata.workOrder.hasBackfillWorker,
          Partner__c: msgData.metadata.workOrder.partnerSfdcId
        },
        contact: {
          UserId: msgData.metadata.sender.id,
          sfdcId: msgData.metadata.sender.sfdcId,
          Gender__c: msgData.metadata.sender.gender,
          id: msgData.metadata.sender.contactId,
          users: {
            sfdcId: msgData.metadata.sender.sfdcId,
            firstname: msgData.metadata.sender.firstname,
            lastname: msgData.metadata.sender.lastname,
            profileImage: msgData.metadata.sender.profileImage,
            url: msgData.metadata.sender.url,
            accessType: msgData.metadata.sender.accessType,
            userTypeId: msgData.metadata.sender.userTypeId,
            id: msgData.metadata.sender.id
          }
        },
        context: msgData.metadata.context
      }
    } else {
      return {}
    }

  }

  /**
   * Pushes an incoming socket message to subscribers, increments the unread badge count, and
   * adds it to the notification list. Split out from the socket.on('message', ...) wiring so
   * it can be exercised directly in tests without needing a live socket.io connection.
   */
  handleIncomingMessage(msgData: any) {
    const appData = JSON.parse(localStorage.getItem('appData'));
    const messageId = msgData.id;
    const currentUserId = appData.user.id;   
    // Always process the actual message
    this.newMessage.next(this.formateMessageObj(msgData)); 
    // Don't create notification for messages sent by current user
    if (msgData.senderId == currentUserId) {
      return;
    }
    // Vendors only see billing (purchase-order) messages, so only list those notifications for them
    const commentType = msgData.metadata && msgData.metadata.commentType;
    if (commentType !== 'billing') { //appData.user.accessType === 'vendor' && 
      return;
    }
    

    // Check if notification already exists
    const alreadyExists = this.notificationLists.some(
      item => item.id === messageId
    );

   // console.log("==old notification===",alreadyExists)

    // Only add notification if it doesn't already exist
    if (!alreadyExists) {
      const restructureMessage = this.restructureMessageNotification(msgData);
      this.notificationLists.unshift(restructureMessage);
      this.allNotificationLists.unshift(restructureMessage);
      if (this.notificationLists.length > this.MAX_NOTIFICATION_LIST) {
        this.notificationLists = this.notificationLists.slice(
          0,
          this.MAX_NOTIFICATION_LIST
        );
      }
      // Emit top 10 notifications
      if (localStorage.getItem('isNotificationEnabled') && localStorage.getItem('isNotificationEnabled') === 'true'  && this.isStoredRoomJoined(msgData)) {
        this.messageHeaderNotificationList.next(this.notificationLists);
        this.notificationCount += 1;
        this.totalUnreadMessageNotification.next(
          this.notificationCount
        );
      }
      // Emit all notifications
      this.messageNotificationList.next(this.allNotificationLists);
    }
  }

    /**
   * True when at least one room saved in localStorage 'roomNames' has been joined on the
   * current socket connection, so header notifications are only shown for rooms we're in.
   */
  isStoredRoomJoined(msgData: any): boolean {
    if (!this.joinedRooms || !this.joinedRooms.size || !msgData) {
      return false;
    }
    const roomBySubModel = {
      soMessage: 'JOB-BILLING-SO-NOTIFICATION',
      poMessage: 'JOB-BILLING-PO-NOTIFICATION'
    };
    const room = roomBySubModel[msgData.subModelName];
    if (!room) {
      return false;
    }
    let storedRooms = [];
    try {
      storedRooms = JSON.parse(localStorage.getItem('roomNames')) || [];
    } catch (e) {
      return false;
    }
    return Array.isArray(storedRooms) && storedRooms.includes(room) && this.joinedRooms.has(room);
  }
  /**
   * Reshapes a raw job-message-console payload into the same {id, entity_name, title,
   * date_created, marked_read} shape app-notification-header already renders for the general
   * notification list. id must be the real backend messages.id (not a synthesized string) since
   * readMessageNotification() sends it straight to PATCH /messages/{id}/read.
   */
  restructureMessageNotification(msgData: any): any {
    const metadata = msgData.metadata || {};
    return {
      id: msgData.id,
      entity_name: `${(msgData.subModelId) ? (msgData.subModelId.toUpperCase()):''} ${msgData.modelName}`,
      title: msgData.content,
      details: msgData.content,
      //jobNumber: metadata.job && metadata.job.ironJobNum,
      date_created: msgData.sentAt || msgData.createdAt || new Date(),
      marked_read: false,
      source: 'message',
      senderId:msgData.senderId,
      // Needed by app-notification-header to route to the right PO/SO tab and thread -
      // subModelName is 'poMessage'/'soMessage' (see sg-svc-backend comment.js prepareSQSNotificationObj),
      // modelName/modelId identify the Job/Case thread to select (same identity unshiftDataInList() uses).
      subModelName: msgData.subModelName,
      modelName: msgData.modelName,
      modelId: msgData.modelId,
      jobId: metadata.jobId
    };
  }  

  /**
   * Joins the room for the given console (e.g. 'soMessage' / 'poMessage') so this client only
   * receives events relevant to the console currently open, mirroring registerConversation().
   */
  registerConsole(consoleType: string) {
    clearTimeout(this.interval);
    let joinRooms = localStorage.getItem('roomNames') ? JSON.parse(localStorage.getItem('roomNames')) :[];
    const roomName = (consoleType == 'poMessage') ? 'JOB-BILLING-PO-NOTIFICATION' : 'JOB-BILLING-SO-NOTIFICATION';
    joinRooms.push(roomName);
    if (joinRooms && joinRooms.length) {
      this.interval = setTimeout(() => {        
      this.joinNotificationSocketRoom(joinRooms);
      }, 5000);
    }
    
  }

  getMessage(commentType?: string): Observable<any> {
    // No commentType passed - emit every message
    if (!commentType) {
      return this.newMessage.asObservable();
    }
    return this.newMessage.asObservable().pipe(
      filter(message => message && message.commentType === commentType)
    );
  }

  getConnectionStatus(): Observable<boolean> {
    return this.socketConnection.asObservable();
  }

  socketDisconnect() {
    if (this.socket) {
      this.socket.disconnect();
    }
  }

  /**
   * Unread count of Job Message Console messages received over the socket since the last reset,
   * consumed by navigation.component.ts / sidebar.component.ts to badge the console entry point.
   */
  getMessageNotificationCount(): Observable<number> {
    return this.totalUnreadMessageNotification.asObservable();
  }
  //for limited notification
  getMessageHeaderNotificationList(): Observable<any[]> {
    return this.messageHeaderNotificationList.asObservable();
  }

  /**
   * Running list of Job Message Console notifications, consumed by app-notification-header to
   * render them alongside the general notification list.
   */
  getMessageNotificationList(): Observable<any[]> {
    return this.messageNotificationList.asObservable();
  }

  /**
   * Marks a single notification as read, e.g. when its envelope icon is clicked in the header,
   * or the user opens it one by one in notification.component.ts. Updates local state
   * immediately for a responsive UI, then persists it via PATCH /messages/{id}/read.
   */
  readMessageNotification(id: string) {
    const notification = this.notificationLists.find(item => item.id === id);
    if (!notification || notification.marked_read) {
      return;
    }
    notification.marked_read = true;
    this.notificationCount = Math.max(0, this.notificationCount - 1);
    this.totalUnreadMessageNotification.next(this.notificationCount);
    this.messageNotificationList.next(this.notificationLists);

    this.http.patch(`${this.apiBaseUrl}/messages/${id}/read`, {}).subscribe(
      () => { },
      error => {
        console.error('MessageNotificationSocketService: failed to mark message as read', error);
      }
    );
  }

  /**
   * Marks every notification as read, e.g. "Mark All Read" in the header/notification.component.ts,
   * or once the console for the given room/consoleType is opened and its messages are considered
   * read. There's no bulk "mark all read" endpoint - the backend instead deletes every message
   * addressed to the user via DELETE /messages, which has the same effect (nothing left unread).
   */
  readAllMessageNotification() {
    this.notificationLists.forEach(item => item.marked_read = true);
    this.notificationCount = 0;
    this.totalUnreadMessageNotification.next(this.notificationCount);
    this.messageNotificationList.next(this.notificationLists);

    this.http.delete(`${this.apiBaseUrl}/messages`).subscribe(
      () => { },
      error => {
        console.error('MessageNotificationSocketService: failed to delete all messages', error);
      }
    );
  }

  /**
   * @deprecated use readAllMessageNotification(), which also marks the list itself as read.
   */
  resetMessageNotificationCount() {
    this.readAllMessageNotification();
  }

  /**
   * Joins the given room so this client keeps receiving notification events for it. Called from
   * socket.on('connect', ...) with the current user's room - room membership is server-side/
   * session-scoped, so it has to be (re)requested on every connect, not just the first one.
   */
  joinNotificationSocketRoom(roomNames: any) {  
    if (roomNames.length==0 || !this.socket || !this.socket.connected) {
      return;
    }
    if (!this.joinedRooms) {
      this.joinedRooms = new Set();
    }
    if (roomNames.length > 0) {
      roomNames.forEach((room:any) => {
        // Already a member of this room on the current connection - no need to emit join again
        // if (!room || this.joinedRooms.has(room)) {
        //   return;
        // }
        // console.log("===========================joined_room===========================", room)
        this.socket.emit('join_room', room);
        this.joinedRooms.add(room);
      })      
    }

   
  }

  /**
   * Tells whichever job-message-console page is (or is about to be) mounted which notification
   * to open - the thread to select and, for the internal SO/PO billing page, which tab. Used by
   * app-notification-header instead of a query param so a page that's already open can react
   * without a route reload.
   */
  setPendingSelection(row: any) {
    this.pendingSelection.next(row);
  }

  getPendingSelection(): Observable<any> {
    return this.pendingSelection.asObservable();
  }

  /** Synchronous read of the current value, used to pick the right tab before the first data load. */
  peekPendingSelection(): any {
    return this.pendingSelection.value;
  }

  /** Consumed once applied, so a later unrelated mount of the console doesn't replay it. */
  clearPendingSelection() {
    this.pendingSelection.next(null);
  }

  readMessageModuleWise(moduleName,message) {
    if(moduleName){
      this.readMessageNotification(message.id)
    }

    // this.http.patch(`${this.apiBaseUrl}/messages/${moduleName}/read`, {}).subscribe(
    //   () => { },
    //   error => {
    //     console.error('MessageNotificationSocketService: failed to mark message as read', error);
    //   }
    // );
  }
}
